// src/components/charts/CRMFunnelCharts.jsx
import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import kpiService from '../../services/kpiService';
import { formatCurrency, formatNumber } from '../../utils/formatters';

/**
 * Componente que agrupa los gráficos de CRM y Funnel de Ventas (Imagen 5)
 * - Funnel de ventas (barras horizontales - izquierda)
 * - Principales métricas (tabla - derecha)
 */
const CRMFunnelCharts = ({ fechaInicio, fechaFin, empresaId }) => {
  const [funnelData, setFunnelData] = React.useState(null);
  const [metricasData, setMetricasData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Obtener funnel de ventas
        const funnelResponse = await kpiService.getFunnelVentas({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        // Obtener métricas CRM
        const metricasResponse = await kpiService.getMetricasCRM({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (funnelResponse.success && funnelResponse.data) {
          setFunnelData(funnelResponse.data);
        }

        if (metricasResponse.success && metricasResponse.data) {
          setMetricasData(metricasResponse.data);
        }
      } catch (err) {
        console.error('Error fetching CRM/Funnel data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xs={12} className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3 mb-4">
      {/* Funnel de ventas - Barras horizontales */}
      <Col lg={6}>
        {funnelData && Array.isArray(funnelData) && funnelData.length > 0 ? (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Funnel de ventas</h5>
              <div className="d-flex flex-column gap-4">
                {funnelData.map((item, idx) => {
                  const max = item.max || 1; // Evitar división por 0
                  const percentage = (item.valor / max) * 100;
                  return (
                    <div key={idx}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-medium">{item.etapa || 'Sin nombre'}</span>
                        <span className="fw-semibold">{formatNumber(item.valor || 0)}</span>
                      </div>
                      <div className="position-relative" style={{ height: '32px' }}>
                        <div
                          className="position-absolute w-100 rounded"
                          style={{
                            height: '100%',
                            backgroundColor: '#e5e7eb',
                          }}
                        />
                        <div
                          className="position-absolute rounded"
                          style={{
                            height: '100%',
                            width: `${percentage}%`,
                            backgroundColor: item.color || '#3b82f6',
                            transition: 'width 0.6s ease-out',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Funnel de ventas</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Principales métricas - Tabla */}
      <Col lg={6}>
        {metricasData && Array.isArray(metricasData) && metricasData.length > 0 ? (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Principales métricas</h5>
              <div className="d-flex flex-column gap-3">
                {metricasData.map((metrica, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <div className="small text-muted mb-1">{metrica.metrica || 'Sin nombre'}</div>
                      <div className="h4 mb-0 fw-bold">
                        {metrica.valor || 0}{metrica.unidad || ''}
                      </div>
                    </div>
                    <div className="text-end">
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: metrica.isPositive ? '#10b981' : '#ef4444',
                          fontSize: '0.875rem',
                          padding: '0.5rem 0.75rem'
                        }}
                      >
                        {metrica.isPositive ? '+' : ''}{metrica.variacion || 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Principales métricas</h5>
              <p className="text-muted text-center py-4">No hay datos disponibles</p>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default CRMFunnelCharts;